import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
    Rocket, Flag, Server, Database, CheckCircle2, XCircle, AlertTriangle,
    Play, Plus, ShieldOff, Undo2, Sparkles,
} from "lucide-react";

const STATUS_TONE = {
    operational: "text-emerald-700 bg-emerald-50 border-emerald-200",
    degraded:    "text-amber-800 bg-amber-50 border-amber-200",
    partial_outage: "text-orange-800 bg-orange-50 border-orange-200",
    major_outage:   "text-rose-800 bg-rose-50 border-rose-200",
    maintenance:    "text-blue-800 bg-blue-50 border-blue-200",
};

const RELEASE_STATUS_TONE = {
    development: "bg-stone-100 text-stone-700",
    staging: "bg-blue-50 text-blue-700",
    approval_required: "bg-amber-50 text-amber-800",
    ready: "bg-emerald-50 text-emerald-700",
    deploying: "bg-violet-50 text-violet-700",
    live: "bg-emerald-100 text-emerald-800 border border-emerald-300",
    rolled_back: "bg-rose-50 text-rose-700",
    failed: "bg-rose-100 text-rose-800",
};

function Section({ title, subtitle, right, children }) {
    return (
        <div className="mt-8">
            <div className="flex items-end justify-between mb-3">
                <div>
                    <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500">{title}</h2>
                    {subtitle && <p className="text-sm text-stone-600 mt-1">{subtitle}</p>}
                </div>
                {right}
            </div>
            {children}
        </div>
    );
}

function EnvironmentBanner({ env }) {
    if (!env || env.is_production) return null;
    return (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-2.5 mb-6 flex items-center gap-2" data-testid="env-banner">
            <AlertTriangle size={14} className="text-amber-700" />
            <span className="text-[12.5px] text-amber-900">
                <span className="font-bold uppercase tracking-widest">{env.name}</span> environment · not for production data
            </span>
        </div>
    );
}

function CheckRow({ label, ok, onToggle }) {
    const Icon = ok ? CheckCircle2 : XCircle;
    return (
        <button onClick={onToggle}
                className={`w-full text-left flex items-center gap-2 py-1 text-[12.5px] ${ok ? "text-emerald-700" : "text-stone-500"}`}>
            <Icon size={13} /> {label}
        </button>
    );
}

function FeatureFlagRow({ f, onSave, onKill }) {
    const [enabled, setEnabled] = useState(f.enabled);
    const [pct, setPct] = useState(f.percentage || 0);
    const [kind, setKind] = useState(f.target_kind || "everyone");
    return (
        <div className="border-b border-stone-100 last:border-b-0 px-5 py-4" data-testid={`ff-row-${f.key}`}>
            <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-stone-900 truncate">{f.name} <span className="text-stone-400 font-normal">· {f.key}</span></div>
                    {f.description && <div className="text-[11.5px] text-stone-500 mt-0.5">{f.description}</div>}
                </div>
                <select value={kind} onChange={(e) => setKind(e.target.value)}
                        data-testid={`ff-kind-${f.key}`}
                        className="text-[12px] border border-stone-200 rounded px-2 py-1">
                    {["everyone","internal_admin","founder","beta_users","selected_users","selected_plans","selected_countries","percentage"].map(k => <option key={k} value={k}>{k.replace(/_/g," ")}</option>)}
                </select>
                {kind === "percentage" && (
                    <input type="number" min={0} max={100} value={pct}
                           onChange={(e) => setPct(Number(e.target.value))}
                           className="w-16 text-[12px] border border-stone-200 rounded px-2 py-1" />
                )}
                <label className="inline-flex items-center gap-1.5 text-[12px]">
                    <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid={`ff-enabled-${f.key}`} />
                    Enabled
                </label>
                <button onClick={() => onSave({ ...f, enabled, percentage: pct, target_kind: kind })}
                        data-testid={`ff-save-${f.key}`}
                        className="text-[12px] font-semibold px-3 py-1 rounded bg-stone-900 text-white hover:bg-stone-800">
                    Save
                </button>
                <button onClick={() => onKill(f.key)}
                        data-testid={`ff-kill-${f.key}`}
                        className="text-[12px] font-semibold px-3 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1">
                    <ShieldOff size={11} /> Kill
                </button>
            </div>
        </div>
    );
}

const CHECKLIST_LABELS = {
    db_backup_ok: "Database backup successful",
    migrations_tested: "Migrations tested on Staging",
    auth_ok: "Authentication verified",
    workspace_isolation_ok: "Workspace isolation verified",
    billing_ok: "Billing verified",
    webhook_ok: "Paddle webhook verified",
    email_ok: "Email delivery verified",
    documents_ok: "Document upload verified",
    mobile_ok: "Mobile tested",
    security_ok: "Security checks passed",
    accessibility_ok: "Accessibility checks passed",
    rollback_plan_ok: "Rollback plan available",
};

export default function SuperAdminReleases() {
    const [env, setEnv] = useState(null);
    const [flags, setFlags] = useState([]);
    const [releases, setReleases] = useState([]);
    const [health, setHealth] = useState(null);
    const [backup, setBackup] = useState(null);
    const [newRel, setNewRel] = useState({ version: "", features: "" });
    const [newFlag, setNewFlag] = useState({ key: "", name: "" });

    const load = () => {
        api.get("/ops/environment").then((r) => setEnv(r.data)).catch(() => {});
        api.get("/ops/feature-flags").then((r) => setFlags(r.data.rows || [])).catch(() => {});
        api.get("/ops/releases").then((r) => setReleases(r.data.rows || [])).catch(() => {});
        api.get("/ops/system-health").then((r) => setHealth(r.data)).catch(() => {});
        api.get("/ops/backup-status").then((r) => setBackup(r.data)).catch(() => {});
    };
    useEffect(load, []);

    const saveFlag = async (f) => { await api.post("/ops/feature-flags", f); load(); };
    const killFlag = async (k) => { await api.delete(`/ops/feature-flags/${k}`); load(); };

    const createFlag = async () => {
        if (!newFlag.key || !newFlag.name) return;
        await api.post("/ops/feature-flags", { ...newFlag, target_kind: "internal_admin", enabled: false });
        setNewFlag({ key: "", name: "" }); load();
    };

    const createRelease = async () => {
        if (!newRel.version) return;
        await api.post("/ops/releases", {
            version: newRel.version,
            features: newRel.features ? newRel.features.split(",").map(s => s.trim()) : [],
            status: "development",
        });
        setNewRel({ version: "", features: "" }); load();
    };

    const toggleCheck = async (rel, key) => {
        const checklist = { ...(rel.checklist || {}), [key]: !rel.checklist?.[key] };
        await api.post("/ops/releases", { ...rel, checklist });
        load();
    };

    const advanceStatus = async (rel, next) => {
        await api.post("/ops/releases", { ...rel, status: next });
        load();
    };

    const approve = async (v) => {
        try {
            await api.post(`/ops/releases/${v}/approve`);
            load();
        } catch (e) {
            alert(e.response?.data?.detail || "Approval blocked");
        }
    };

    return (
        <div className="max-w-[1400px] mx-auto" data-testid="releases-page">
            <div className="mb-6">
                <div className="eyebrow text-amber-700">Release Governance</div>
                <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                    Releases · Flags · Health
                </h1>
                <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                    Dev → Staging → Production lifecycle · feature flags · disaster-recovery status. No production deployment can be marked Ready while a Blocker exists.
                </p>
            </div>

            <EnvironmentBanner env={env} />

            {/* ═══ System Health ═══ */}
            <Section title="System Health" subtitle="Real component statuses. Never fabricated.">
                {!health ? <p className="text-sm text-stone-500">Loading…</p> : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="health-grid">
                        {health.components.map((c) => (
                            <div key={c.key}
                                 data-testid={`health-${c.key}`}
                                 className={`rounded-xl border px-3 py-2.5 flex items-center gap-2 ${STATUS_TONE[c.status] || STATUS_TONE.operational}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                <div className="min-w-0">
                                    <div className="text-[11.5px] font-semibold truncate">{c.label}</div>
                                    <div className="text-[10px] uppercase tracking-widest opacity-80">{c.status.replace(/_/g," ")}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Section>

            {/* ═══ Backup & Recovery ═══ */}
            <Section title="Backup & Recovery">
                <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="backup-panel">
                    {!backup ? <p className="text-sm text-stone-500">Loading…</p> : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Last database backup</div>
                                    <div className="text-[15px] font-semibold text-stone-900 mt-1">
                                        {backup.last_backup?.created_at ? new Date(backup.last_backup.created_at).toLocaleString() : "Not recorded"}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Last restore test</div>
                                    <div className="text-[15px] font-semibold text-stone-900 mt-1">
                                        {backup.last_restore_test?.created_at ? new Date(backup.last_restore_test.created_at).toLocaleString() : "Not tested"}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Recovery objectives</div>
                                    <div className="text-[12.5px] text-stone-700 mt-1">
                                        RPO {backup.policy.database.target_rpo_hours}h · RTO {backup.policy.database.target_rto_hours}h
                                    </div>
                                </div>
                            </div>
                            {backup.hint && (
                                <div className="mt-4 text-[12.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2">
                                    <AlertTriangle size={13} /> {backup.hint}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Section>

            {/* ═══ Feature Flags ═══ */}
            <Section title="Feature flags" subtitle="Progressive rollout. Emergency kill-switch available." right={
                <div className="flex items-center gap-2">
                    <input value={newFlag.key} onChange={(e) => setNewFlag({ ...newFlag, key: e.target.value })}
                           placeholder="flag_key" data-testid="ff-new-key"
                           className="text-[12px] border border-stone-200 rounded px-2 py-1 w-32" />
                    <input value={newFlag.name} onChange={(e) => setNewFlag({ ...newFlag, name: e.target.value })}
                           placeholder="Human name" data-testid="ff-new-name"
                           className="text-[12px] border border-stone-200 rounded px-2 py-1 w-40" />
                    <button onClick={createFlag} data-testid="ff-create-btn"
                            className="inline-flex items-center gap-1 text-[12px] px-3 py-1 rounded bg-stone-900 text-white">
                        <Plus size={11} /> Add flag
                    </button>
                </div>
            }>
                <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="ff-list">
                    {flags.length === 0 ? (
                        <div className="p-6 text-center text-sm text-stone-500">No feature flags yet — add one above.</div>
                    ) : flags.map((f) => (
                        <FeatureFlagRow key={f.key} f={f} onSave={saveFlag} onKill={killFlag} />
                    ))}
                </div>
            </Section>

            {/* ═══ Releases ═══ */}
            <Section title="Releases" subtitle="Every production release passes the 12-point checklist before it can be marked Ready." right={
                <div className="flex items-center gap-2">
                    <input value={newRel.version} onChange={(e) => setNewRel({ ...newRel, version: e.target.value })}
                           placeholder="v2026.08.01" data-testid="rel-new-version"
                           className="text-[12px] border border-stone-200 rounded px-2 py-1 w-32" />
                    <input value={newRel.features} onChange={(e) => setNewRel({ ...newRel, features: e.target.value })}
                           placeholder="Command Centre, Growth" data-testid="rel-new-features"
                           className="text-[12px] border border-stone-200 rounded px-2 py-1 w-64" />
                    <button onClick={createRelease} data-testid="rel-create-btn"
                            className="inline-flex items-center gap-1 text-[12px] px-3 py-1 rounded bg-stone-900 text-white">
                        <Plus size={11} /> Add
                    </button>
                </div>
            }>
                <div className="space-y-3" data-testid="rel-list">
                    {releases.length === 0 && <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 p-6 text-center text-sm text-stone-500">Track your first release above.</div>}
                    {releases.map((r) => (
                        <div key={r.version} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`rel-row-${r.version}`}>
                            <div className="flex items-center gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[15px] font-semibold text-stone-900">{r.version}</span>
                                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${RELEASE_STATUS_TONE[r.status] || "bg-stone-100 text-stone-700"}`}>
                                            {(r.status || "development").replace(/_/g," ")}
                                        </span>
                                        {r.blocker && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">Blocker</span>}
                                    </div>
                                    <div className="text-[11.5px] text-stone-500 mt-0.5">
                                        {(r.features || []).join(" · ") || "No features listed"}
                                    </div>
                                </div>
                                <select value={r.status} onChange={(e) => advanceStatus(r, e.target.value)}
                                        className="text-[12px] border border-stone-200 rounded px-2 py-1">
                                    {["development","staging","approval_required","ready","deploying","live","rolled_back","failed"].map(s =>
                                        <option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
                                </select>
                                <button onClick={() => approve(r.version)}
                                        data-testid={`rel-approve-${r.version}`}
                                        className="inline-flex items-center gap-1 text-[12px] px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white">
                                    <CheckCircle2 size={11} /> Approve
                                </button>
                                <button onClick={() => api.post(`/ops/releases/${r.version}/rollback`).then(load)}
                                        data-testid={`rel-rollback-${r.version}`}
                                        className="inline-flex items-center gap-1 text-[12px] px-3 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50">
                                    <Undo2 size={11} /> Rollback
                                </button>
                            </div>
                            {/* Checklist */}
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
                                {Object.entries(CHECKLIST_LABELS).map(([k, label]) => (
                                    <CheckRow key={k}
                                              label={label}
                                              ok={!!r.checklist?.[k]}
                                              onToggle={() => toggleCheck(r, k)} />
                                ))}
                            </div>
                            {r.blocker && (
                                <div className="mt-3 text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-1.5">
                                    {r.blocker}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
}
